"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { useTranslations } from "next-intl"
import * as Sentry from "@sentry/nextjs"
import { loginWithPassword } from "@/app/actions/postLoginActions"
import { requestEmailCode, verifyEmailCode } from "@/app/actions/emailCodeActions"
import { getAuthClient } from "@/lib/supabase/browser"
import { nativeSafeNavigate } from "@/lib/capacitor/nav"
import { reportCaughtVersionSkew } from "@/lib/deploy/versionSkew"
import { LOGIN_FAILURE_MESSAGE_KEY } from "@/lib/auth/loginFailure"
import { EmailInput } from "@/components/ui"
import {
  createLoginHandlers,
  type FieldError,
  type LoginFailure,
  type LoginStep,
} from "./loginHandlers"
import type { LoginFormProps } from "./searchParams"

type CodeStep = "idle" | "sent"

const SUPPORT_EMAIL = "hola@useredy.com"

// Si tras `navigate` seguimos en la página (la navegación se bloqueó o falló),
// los botones vuelven a la vida en vez de quedarse en «Entrando...».
const REDIRECT_WATCHDOG_MS = 15_000

/**
 * Formulario de /login. Lo renderiza el server component (page.tsx) con los
 * parámetros de la URL ya resueltos, así que sale pintado en el HTML del
 * servidor. Los handlers viven en ./loginHandlers.ts (try/catch/finally, S5-4).
 */
export function LoginForm({
  next,
  prefilledEmail,
  fromSignup,
  noBusinessFromOauth,
  oauthFailedFromUrl,
}: LoginFormProps) {
  const t = useTranslations("auth.login")

  // --- Code flow state ---
  const [codeStep, setCodeStep] = useState<CodeStep>("idle")
  const [codeEmail, setCodeEmail] = useState(prefilledEmail)
  const [code, setCode] = useState("")
  const [codeError, setCodeError] = useState<FieldError | null>(null)

  // --- Password form state (behind toggle) ---
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [email, setEmail] = useState(prefilledEmail)
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [passwordError, setPasswordError] = useState<FieldError | null>(null)

  // --- Shared ---
  const [oauthError, setOauthError] = useState(false)
  const [pending, setPending] = useState<LoginStep | null>(null)
  const [failure, setFailure] = useState<LoginFailure | null>(null)
  const [redirecting, setRedirecting] = useState(false)

  useEffect(() => {
    if (!redirecting) return
    const id = setTimeout(() => setRedirecting(false), REDIRECT_WATCHDOG_MS)
    return () => clearTimeout(id)
  }, [redirecting])

  // Se crean en cada render a propósito: `read()` cierra sobre el estado de
  // ESTE render, que es el que ve el evento que los dispara.
  const handlers = createLoginHandlers({
    actions: {
      signInWithGoogle: () =>
        getAuthClient().auth.signInWithOAuth({
          provider: "google",
          options: { redirectTo: `${window.location.origin}/callback` },
        }),
      requestEmailCode,
      verifyEmailCode,
      loginWithPassword,
    },
    read: () => ({ codeEmail, code, email, password, next }),
    setPending,
    setFailure,
    setCodeError,
    setPasswordError,
    setOauthError,
    onCodeSent: () => {
      setCode("")
      setCodeError(null)
      setCodeStep("sent")
    },
    navigate: (path) => {
      // Navegación completa (no router.push) para que el servidor vea las
      // cookies recién puestas. En iOS pasa por /tablet?to= (ver lib/capacitor/nav).
      setRedirecting(true)
      nativeSafeNavigate(path)
    },
    report: (err, { step, kind }) => {
      Sentry.captureException(err, {
        level: kind === "server" ? "warning" : "error",
        tags: { surface: "login", login_step: step, login_failure: kind },
        // `server` = respuesta no-RSC (403 de la plataforma a bots, 5xx): se
        // agrupa aparte de REDY-X para poder silenciarla sin tapar lo demás.
        ...(kind === "server" ? { fingerprint: ["login", "server-response", step] } : {}),
      })
    },
    isOnline: () => navigator.onLine,
    onVersionSkew: (err) => {
      reportCaughtVersionSkew(err)
    },
  })

  const busy = pending !== null || redirecting

  function fieldErrorText(error: FieldError): string {
    if (error.key === "rateLimited") return t("rateLimited", { seconds: error.seconds })
    if (error.key === "employeeHint") return t("employeeHint", { supportEmail: SUPPORT_EMAIL })
    return t(error.key)
  }

  function onSubmit(step: LoginStep) {
    return (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      void handlers[step]()
    }
  }

  function clearPasswordError() {
    if (passwordError) setPasswordError(null)
  }

  const inputBase = [
    "w-full px-4 py-3 rounded-xl border text-[14px] text-[var(--redy-charcoal)] placeholder:text-[var(--redy-faint)]",
    "outline-none transition-colors",
    "border-[var(--redy-border)] bg-[var(--redy-surface)] focus:border-[var(--redy-charcoal)]",
  ].join(" ")

  const inputError = [
    "w-full px-4 py-3 rounded-xl border text-[14px] text-[var(--redy-charcoal)] placeholder:text-[var(--redy-faint)]",
    "outline-none transition-colors",
    "border-[var(--redy-error)] bg-[var(--redy-error-bg)] focus:border-[var(--redy-error)]",
  ].join(" ")

  const primaryButton =
    "w-full py-4 rounded-2xl text-white text-[15px] font-black tracking-wide transition-all active:scale-[0.99] active:opacity-90 disabled:opacity-60 cursor-pointer disabled:cursor-not-allowed"
  const primaryStyle = { background: "var(--redy-gradient)", boxShadow: "0 8px 24px rgba(215,38,61,0.22)" }

  const label = (step: LoginStep, idle: string, working: string) =>
    pending === step ? working : redirecting && (step === "verify_code" || step === "password") ? t("redirecting") : idle

  return (
    <>
      <h2 className="text-[22px] font-bold text-[var(--redy-charcoal)] mb-1">{t("title")}</h2>
      <p className="text-[13px] text-[var(--redy-secondary)] mb-8">{t("subtitle")}</p>

      {fromSignup && (
        <div
          className="mb-6 rounded-xl p-3 text-[13px]"
          style={{
            background: "var(--redy-amber-bg)",
            border: "1px solid var(--redy-border)",
            color: "var(--redy-amber-deep)",
          }}
        >
          {t("fromSignupBanner")}
        </div>
      )}

      {/* S5-4: fallo de red / servidor / versión en cualquier paso. Siempre con
          salida: reintentar el mismo paso, o recargar si hay versión nueva. */}
      {failure && (
        <div
          role="alert"
          className="mb-5 rounded-xl p-3 text-[13px] animate-fade-slide-up"
          style={{
            background: "var(--redy-error-bg)",
            border: "1px solid var(--redy-border)",
            color: "var(--redy-charcoal)",
          }}
        >
          <p className="font-medium mb-2">{t(LOGIN_FAILURE_MESSAGE_KEY[failure.kind])}</p>
          {failure.kind === "version_skew" ? (
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="text-[13px] font-semibold cursor-pointer"
              style={{ color: "var(--redy-red)" }}
            >
              {t("reload")}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void handlers[failure.step]()}
              disabled={busy}
              className="text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: "var(--redy-red)" }}
            >
              {t("retry")}
            </button>
          )}
        </div>
      )}

      {/* Google OAuth — primary CTA */}
      <button
        type="button"
        onClick={() => void handlers.google()}
        disabled={busy}
        className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border text-[14px] font-semibold text-[var(--redy-charcoal)] bg-[var(--redy-surface)] transition-all hover:bg-[var(--redy-surface-2)] active:scale-[0.99] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ border: "1px solid var(--redy-border)" }}
      >
        {/* Google "G" icon — colores de marca de Google, no tokens de Redy */}
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
          <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
          <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
          <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
        </svg>
        {pending === "google" ? t("googleConnecting") : t("googleButton")}
      </button>

      {/* oauthError: lo pone el handler (el proveedor devolvió error) o el
          callback vía ?error=oauth_failed */}
      {(oauthError || oauthFailedFromUrl) && (
        <p className="mt-2 text-[12px] font-medium animate-fade-slide-up" style={{ color: "var(--redy-error)" }}>
          {t("oauthError")}
        </p>
      )}

      {/* Volvió de Google con sesión pero sin negocio. NO es un error de Google
          —el acceso funcionó— así que se pinta en ámbar y no en rojo: lo que
          falta es decirle QUÉ hacer, que es entrar con el correo de su cuenta
          por el bloque de abajo. */}
      {noBusinessFromOauth && (
        <p
          className="mt-3 text-[12px] leading-[1.5] font-medium animate-fade-slide-up rounded-xl px-3 py-2.5"
          style={{
            color: "var(--redy-charcoal)",
            background: "var(--redy-amber-bg)",
            border: "1px solid var(--redy-border)",
          }}
        >
          {t("googleNoBusiness", { supportEmail: SUPPORT_EMAIL })}
        </p>
      )}

      {/* Divider */}
      <div className="flex items-center gap-3 my-5">
        <span className="flex-1 h-px" style={{ background: "var(--redy-border)" }} />
        <span className="text-[12px] font-medium" style={{ color: "var(--redy-muted)" }}>
          {t("orDivider")}
        </span>
        <span className="flex-1 h-px" style={{ background: "var(--redy-border)" }} />
      </div>

      {/* Code flow: step 1 — email entry */}
      {codeStep === "idle" ? (
        <form onSubmit={onSubmit("send_code")} noValidate>
          <div className="mb-3">
            <label htmlFor="login-code-email" className="block mb-1.5 text-[12px] font-semibold uppercase tracking-wide" style={{ color: "var(--redy-charcoal)" }}>
              {t("emailLabel")}
            </label>
            <EmailInput
              id="login-code-email"
              value={codeEmail}
              onChange={(v) => { setCodeEmail(v); if (codeError) setCodeError(null) }}
              placeholder={t("emailPlaceholder")}
              autoFocus
              inputClassName={codeError ? inputError : inputBase}
            />
          </div>
          {codeError && (
            <p className="mb-3 text-[12px] font-medium animate-fade-slide-up" style={{ color: "var(--redy-error)" }}>
              {fieldErrorText(codeError)}
            </p>
          )}
          <button type="submit" disabled={busy} className={primaryButton} style={primaryStyle}>
            {label("send_code", t("sendCodeButton"), t("sendingCode"))}
          </button>
        </form>
      ) : (
        /* Code flow: step 2 — enter code */
        <div>
          {/* Sent confirmation banner */}
          <div
            className="rounded-xl p-3 mb-4 text-[13px]"
            style={{
              background: "var(--redy-success-bg)",
              border: "1px solid var(--redy-border)",
              color: "var(--redy-secondary)",
            }}
          >
            <p className="font-semibold mb-0.5" style={{ color: "var(--redy-charcoal)" }}>
              {t("codeSentTitle")}
            </p>
            <p>{t("codeSentDescription")}</p>
          </div>

          <form onSubmit={onSubmit("verify_code")} noValidate>
            <div className="mb-3">
              <label htmlFor="login-otp-code" className="block mb-1.5 text-[12px] font-semibold uppercase tracking-wide" style={{ color: "var(--redy-charcoal)" }}>
                {t("codeLabel")}
              </label>
              <input
                id="login-otp-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={10}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 10))
                  if (codeError) setCodeError(null)
                }}
                autoFocus
                className={codeError ? inputError : inputBase}
              />
            </div>

            {codeError && (
              <p className="mb-3 text-[12px] font-medium animate-fade-slide-up" style={{ color: "var(--redy-error)" }}>
                {fieldErrorText(codeError)}
              </p>
            )}

            <button type="submit" disabled={busy || code.length < 4} className={primaryButton} style={primaryStyle}>
              {label("verify_code", t("verifyCodeButton"), t("verifyingCode"))}
            </button>
          </form>

          {/* Resend / change email */}
          <div className="flex items-center justify-center gap-4 mt-4">
            <button
              type="button"
              onClick={() => { setCode(""); void handlers.resend_code() }}
              disabled={busy}
              className="text-[13px] font-medium transition-colors disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
              style={{ color: "var(--redy-muted)" }}
            >
              {pending === "resend_code" ? t("sendingCode") : t("resendCode")}
            </button>
            <span className="text-[var(--redy-border-l)] select-none">·</span>
            <button
              type="button"
              onClick={() => { setCodeStep("idle"); setCode(""); setCodeError(null); setFailure(null) }}
              disabled={busy}
              className="text-[13px] font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: "var(--redy-muted)" }}
            >
              {t("changeEmail")}
            </button>
          </div>
        </div>
      )}

      {/* "Other ways to sign in" toggle */}
      <div className="mt-6">
        <button
          type="button"
          onClick={() => setShowPasswordForm((v) => !v)}
          className="w-full text-center text-[13px] font-medium transition-colors cursor-pointer"
          style={{ color: "var(--redy-muted)" }}
        >
          {t("otherWays")}
          <span className="ml-1" aria-hidden="true">{showPasswordForm ? "↑" : "↓"}</span>
        </button>

        {showPasswordForm && (
          <div className="mt-5">
            <form onSubmit={onSubmit("password")} noValidate>
              <div className="mb-4">
                <label htmlFor="login-email" className="block mb-1.5 text-[12px] font-semibold uppercase tracking-wide" style={{ color: "var(--redy-charcoal)" }}>
                  {t("emailLabel")}
                </label>
                <EmailInput
                  id="login-email"
                  value={email}
                  onChange={(v) => { setEmail(v); clearPasswordError() }}
                  placeholder={t("emailPlaceholder")}
                  inputClassName={passwordError ? inputError : inputBase}
                />
              </div>

              <div className="mb-2">
                <label htmlFor="login-password" className="block mb-1.5 text-[12px] font-semibold uppercase tracking-wide" style={{ color: "var(--redy-charcoal)" }}>
                  {t("passwordLabel")}
                </label>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); clearPasswordError() }}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className={(passwordError ? inputError : inputBase) + " pr-11"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: "var(--redy-muted)" }}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-[18px] h-[18px]">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-[18px] h-[18px]">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {passwordError && (
                <p className="mt-2 text-[12px] font-medium animate-fade-slide-up" style={{ color: "var(--redy-error)" }}>
                  {fieldErrorText(passwordError)}
                </p>
              )}

              <div className="mt-2 text-right">
                <Link href="/forgot-password" prefetch={false} className="text-[12px] font-medium transition-colors" style={{ color: "var(--redy-muted)" }}>
                  {t("forgotPassword")}
                </Link>
              </div>

              <button type="submit" disabled={busy} className={"mt-5 " + primaryButton} style={primaryStyle}>
                {label("password", t("submit"), t("submitting"))}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* M-25 (audit 2026-05-27): users who land on /login by mistake
          had no way to reach signup without going back to the
          marketing site. */}
      <p className="mt-6 text-center text-[13px]" style={{ color: "var(--redy-secondary)" }}>
        {t("noAccount")}{" "}
        <Link href="/signup" prefetch={false} className="font-semibold transition-colors" style={{ color: "var(--redy-red)" }}>
          {t("createAccount")}
        </Link>
      </p>
    </>
  )
}
